import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, PhoneCall, MapPin, AlertTriangle, Briefcase, Stethoscope, Users, Heart, ClipboardList, Clock, TrendingDown, HeartHandshake, Award } from 'lucide-react';
const capabilities = [{
  icon: PhoneCall,
  name: 'Telephonic Case Management',
  description: 'Experienced RN case managers coordinate care, communicate with providers, and remove obstacles to recovery — all via phone.'
}, {
  icon: MapPin,
  name: 'Field & On-Site CM',
  description: 'In-person nurse presence at provider appointments and facility visits when claims require it.'
}, {
  icon: AlertTriangle,
  name: 'Catastrophic CM',
  description: 'Specialized clinical case managers for traumatic brain injury, spinal cord, burn, and other catastrophic claims.'
}, {
  icon: Briefcase,
  name: 'Return-to-Work Coordination',
  description: 'Active collaboration with employers, treating physicians, and injured workers to facilitate modified and full-duty return.'
}, {
  icon: Stethoscope,
  name: 'Treatment Plan Oversight',
  description: 'Ongoing review of medical care to ensure alignment with evidence-based guidelines and recovery goals.'
}, {
  icon: ClipboardList,
  name: 'Vocational Rehabilitation',
  description: "When return to the same job isn't possible, our team coordinates retraining and job placement support."
}, {
  icon: Users,
  name: 'Family & Caregiver Support',
  description: 'Education and resources for family members supporting the injured worker through recovery.'
}, {
  icon: Heart,
  name: 'Behavioral Health Coordination',
  description: 'Recognizing the mental health dimensions of injury — and connecting workers to appropriate support.'
}];
const benefits = [{
  icon: HeartHandshake,
  title: 'Higher satisfaction',
  stat: 'Care',
  description: 'Injured workers report feeling supported and understood throughout their recovery.'
}, {
  icon: Award,
  title: 'Credentialed nurses',
  stat: 'CCM',
  description: 'Case managers hold CCM, COHN, CDMS, or CRRN certifications — and average 15+ years of experience.'
}];
export function CaseManagement() {
  return <div className="bg-brand-paper" data-id="element-480" data-mp="3f40ezjpm45m1">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-481" data-mp="3f6ljkghaoznh">
        <div className="absolute inset-0 opacity-10" data-id="element-482" data-mp="3k33l77hbwhvl">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-483" data-mp="36cgofkv4p225"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-484" data-mp="1wfi1n5ua4sk"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-485" data-mp="6qtezu05gjzc">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-486" data-mp="1rdjfhaeg3pvh">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-487" data-mp="1w6m91ij82zbo">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-488" data-mp="13hf1ju1sizea">
                Managed Care Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-489" data-mp="1iccvgrcl7wxh">
              Case <span className="text-brand-gold italic" data-id="element-490" data-mp="2ttco5o2ukds1">Management</span>
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-491" data-mp="2oui129xuk324">
              Experienced RN case managers guide injured workers from injury to
              recovery — coordinating care, removing barriers, and accelerating
              return to work.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-492" data-mp="17e2oos42buri">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-493" data-mp="1cd5s684e9sxd">
                Request a Consultation <ArrowRight className="w-5 h-5 ml-2" data-id="element-494" data-mp="1majwxagygt9u" />
              </Link>
              <a href="tel:7142591053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-495" data-mp="1r220m6ad57yi">
                <Phone className="w-5 h-5 mr-2" data-id="element-496" data-mp="2039so6madck5" /> 714.259.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-id="element-497" data-mp="24zi87l4eb7ru">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-498" data-mp="2f6t31qi4dhho">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-499" data-mp="2a5x1hjl8q7ff">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform -rotate-2" data-id="element-500" data-mp="2swo9ohthrrs8"></div>
            <img src="https://cdn.magicpatterns.com/uploads/nPgzTMRKLfZYimynhBwrhg/Depositphotos_Hispanic_doctor.jpg" alt="Smiling nurse case manager holding a tablet in a clinical office" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-501" data-mp="2o5oat70tazn3" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-502" data-mp="3bur3d9wzicwd">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-503" data-mp="157i6xodcrjja">
              Why Case Management
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-504" data-mp="r6fnzkhnmpcl">
              Every claim deserves a clinical advocate.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-505" data-mp="hacji7zbyemh">
              When an injured worker has a knowledgeable nurse in their corner,
              recovery accelerates. Treatment plans stay on track. Communication
              breakdowns disappear. Costs come down — not because we cut care,
              but because we coordinate it.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-506" data-mp="2gkkq8r6u6n75">
              Our case managers are experienced RNs, most with 15+ years in
              workers' compensation. They don't just manage paperwork — they
              build trust with injured workers and influence outcomes through
              clinical expertise.
            </p>
            <ul className="space-y-4" data-id="element-507" data-mp="9xwje2j0crv7">
              {['Dedicated nurse case manager per claim', 'Credentialed RNs (CCM, COHN, CRRN, CDMS)', 'Telephonic, field, and catastrophic capabilities', 'Return-to-work focus from day one'].map(item => <li key={item} className="flex items-start" data-id="element-508" data-mp="xm6c7oen2fvu">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-509" data-mp="sq7jjoma2bpm" />
                  <span className="text-gray-800 font-medium" data-id="element-510" data-mp="2zgosyr085t5o">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-brand-paper" data-id="element-511" data-mp="28mc0egbmezz1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-512" data-mp="28liwdn41qokl">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-513" data-mp="2dkvmcgq7sbre">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-514" data-mp="2icwsrm0pb89i">
              Capabilities
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-515" data-mp="2nbhu5orovxzj">
              A complete case management program.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-516" data-mp="1un2gb5kanlps">
              From a single phone call to catastrophic on-site advocacy — we
              match the right level of intervention to each claim.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-517" data-mp="24mjlllvmvxl8">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-518" data-mp="3g0f9pk4ingol" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-519" data-mp="gmfw52caxalb">
                    <Icon className="w-6 h-6" data-id="element-520" data-mp="6re5u36ai2h5" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-521" data-mp="26375y8odjny3">
                    {cap.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-522" data-mp="214nho1mb602k">
                    {cap.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-id="element-523" data-mp="ir06p34lwljg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-524" data-mp="ipp599ymvpvi">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-525" data-mp="epy90qqc8hjl">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-526" data-mp="3n4q364y7dk3m">
              Proven Outcomes
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-527" data-mp="3i6wvt8l5yk1r">
              Better recovery. Lower cost. Every time.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-528" data-mp="3r6jwfs62jltr">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-529" data-mp="33ecwkj14wwpl" data-mpi={i}>
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-530" data-mp="37zucorrtt9lj"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-531" data-mp="2y3rajlwz5ayl" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-532" data-mp="2y3927qx1f6ab">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-533" data-mp="1molwjlre7pzo">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-534" data-mp="1mqot6on1whdd">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-535" data-mp="3pc2bg53nl2v9">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-536" data-mp="3kjr9yxt9l90l"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-537" data-mp="3khy4vmyauwup">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-538" data-mp="3fjdbo7hgtdfr">
            Bring a clinical advocate to every claim.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-539" data-mp="36jq93id021l4">
            Talk to our team about how Arissa Case Management transforms
            outcomes for your injured workers and your bottom line.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-540" data-mp="2wo6i9adalt38">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-541" data-mp="ztjtt9r1kit4">
              Schedule a Meeting
            </Link>
            <a href="tel:7142591053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-542" data-mp="17d2vix82f0r1">
              <Phone className="w-5 h-5 mr-2" data-id="element-543" data-mp="1h5gw3mneq1bm" /> 714.259.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}